import { Navigate } from 'react-router-dom';
import { useConfig } from '../contexts/ConfigContext';
import type { ReactNode } from 'react';

interface FeatureGateProps {
  /** Feature flag name (e.g. PRINT_ORDERING). */
  flag: string;
  /** Where to send the customer when the flag is off for them. */
  fallbackTo: string;
  children: ReactNode;
}

/**
 * Route guard for feature-flagged pages.
 *
 * Waits for the config fetch before deciding, so a hard navigation (reload,
 * browser Back from an external checkout) never bounces off a flag that merely
 * hasn't loaded yet. Rendering the redirect eagerly was the root cause of the
 * "Back from Shopify checkout lands on /orders" bug: on a full page load the
 * default config (all flags off) decided the route before /api/v1/config
 * resolved.
 *
 * Rendering-hint only — the server independently enforces every flag at its
 * own endpoints.
 */
export function FeatureGate({ flag, fallbackTo, children }: FeatureGateProps) {
  const { config, loading } = useConfig();

  if (loading) {
    return (
      <main style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (config.features[flag] !== true) {
    return <Navigate to={fallbackTo} replace />;
  }

  return <>{children}</>;
}
